import {
  chartBlocks,
  insertAfter,
  moveBlock,
  moveSection,
  parseChart,
  parseChartLayout,
  parseKey,
  renderChart,
  replaceBlock,
  replaceBlockSource,
  type ChartLayout,
  type ChartLine,
} from '@church/domain'
import { useDeferredValue, useState } from 'react'
import { useSong, useSongEditor } from './useServices.ts'

/**
 * Editing one arrangement's chart, on its own page: the text and the layout
 * as saved, the drafts as edited, whether they differ, and saving both at
 * once. The page owns the markup; this owns what is being edited.
 *
 * Two ways in, one text:
 * - the desktop's plain text box (`setText`), with its own undo;
 * - the phone's line-at-a-time editor (`blocks` and the edits below), which
 *   rewrites one block of the source at a time and keeps an undo stack of
 *   its own. Lines it does not touch keep their text exactly.
 *
 * Drafts are null until the first change, so they follow the saved chart (a
 * save elsewhere, a refetch) until someone starts editing.
 */
export function useChartEditor(songId: string, arrangementId: string) {
  const song = useSong(songId)
  const editor = useSongEditor(songId)
  const arrangement = song.data?.arrangements.find((a) => a.id === arrangementId) ?? null
  const savedText = arrangement?.chordChart ?? ''
  const savedLayout = parseChartLayout(arrangement?.chartLayout)
  // The text draft and its undo history move together, so two edits in one
  // tap (save the line, then move it) each build on the one before.
  const [edit, setEdit] = useState<{ draft: string | null; past: string[]; future: string[] }>({
    draft: null,
    past: [],
    future: [],
  })
  const [layoutDraft, setLayoutDraft] = useState<ChartLayout | null>(null)
  const [previewKey, setPreviewKey] = useState<string | null>(null)

  const text = edit.draft ?? savedText
  const layout = layoutDraft ?? savedLayout
  const textDirty = edit.draft !== null && edit.draft !== savedText
  const layoutDirty =
    layoutDraft !== null && JSON.stringify(layoutDraft) !== JSON.stringify(savedLayout)

  // The preview lags the typing a little, so a long chart never slows the keys.
  const previewText = useDeferredValue(text)
  const written = parseKey(arrangement?.key ?? '')
  const shown = parseKey(previewKey ?? '') ?? written
  const preview = renderChart(parseChart(previewText), { from: written, to: shown }).lines

  /** A structured edit of the text as it stands: what it was goes on the undo stack. */
  const apply = (change: (current: string) => string) =>
    setEdit((state) => {
      const current = state.draft ?? savedText
      const next = change(current)
      if (next === current) return state
      return { draft: next, past: [...state.past.slice(-99), current], future: [] }
    })
  const reset = () => setEdit({ draft: null, past: [], future: [] })

  return {
    song: song.data ?? null,
    arrangement,
    isPending: song.isPending,
    error: song.error,

    /** The chart as it stands in the editor. */
    text,
    setText: (next: string) => setEdit((state) => ({ ...state, draft: next })),
    layout,
    setLayout: (patch: Partial<ChartLayout>) => setLayoutDraft({ ...layout, ...patch }),

    /** The key the preview shows (the written key unless another is picked). */
    previewKey: shown ? previewKey : null,
    setPreviewKey: (key: string | null) => setPreviewKey(key),
    /** The chart as it reads: parsed, in the preview key. */
    preview,

    /** The chart's blocks: each line with the source lines it came from (the phone editor). */
    blocks: chartBlocks(text),
    /** A block replaced by an edited line, or removed (null). */
    replaceLine: (index: number, line: ChartLine | null) =>
      apply((current) => replaceBlock(current, index, line)),
    /** A block's source replaced by typed text. */
    replaceSource: (index: number, source: string) =>
      apply((current) => replaceBlockSource(current, index, source)),
    insertAfter: (index: number, lines: ChartLine[]) =>
      apply((current) => insertAfter(current, index, lines)),
    moveLine: (index: number, delta: -1 | 1) =>
      apply((current) => moveBlock(current, index, delta)),
    moveSection: (index: number, delta: -1 | 1) =>
      apply((current) => moveSection(current, index, delta)),
    canUndo: edit.past.length > 0,
    canRedo: edit.future.length > 0,
    undo: () =>
      setEdit((state) => {
        const previous = state.past[state.past.length - 1]
        if (previous === undefined) return state
        return {
          draft: previous,
          past: state.past.slice(0, -1),
          future: [state.draft ?? savedText, ...state.future],
        }
      }),
    redo: () =>
      setEdit((state) => {
        const next = state.future[0]
        if (next === undefined) return state
        return {
          draft: next,
          past: [...state.past, state.draft ?? savedText],
          future: state.future.slice(1),
        }
      }),

    /** Edited and not saved: the text, the layout, or both. */
    dirty: textDirty || layoutDirty,
    discard: () => {
      reset()
      setLayoutDraft(null)
    },
    save: (options?: { onSuccess?: () => void }) => {
      if (!arrangement) return
      editor.updateArrangement.mutate(
        {
          id: arrangement.id,
          ...(textDirty ? { chordChart: text } : {}),
          ...(layoutDirty ? { chartLayout: layout } : {}),
        },
        {
          onSuccess: () => {
            reset()
            setLayoutDraft(null)
            options?.onSuccess?.()
          },
        },
      )
    },
    saving: editor.updateArrangement.isPending,
    saveError: editor.updateArrangement.error,
  }
}

export type ChartEditor = ReturnType<typeof useChartEditor>
